import { displayTimelineAnchors, narrationTimelineAnchors, readingTimepoints, readingPositionSeconds, readingTimelineAnchors, splitSubtitleSentences, spokenSentenceProgress, subtitleSentenceAt,
    subtitleSentenceStarts, type NarrationTimepoint, type SubtitleSentence, type TimelineAnchor } from '@/_pure/narrationTimeline';
import { reportEpisodeDiagnostic } from '@/episodeDiagnostics';
import { narrationSilence, type NarrationSilence } from '@/narrationSilence';

/** One beat's narration timeline: the same cues and sentences whether or not the narration is audible. */
export type EpisodeNarrationCue = {
    key: string;
    /** The spoken text; cue phrases refer to it. */
    text: string;
    displayText?: string;
    narrationTextMap?: readonly { spokenOffset: number; displayOffset: number }[];
    /** A recording drives the timeline (muted without sound); without one, the timeline is a timed reading. */
    audioUrl?: string;
    sound: boolean;
    readingMs: number;
    narrationPauses?: readonly { offset: number; milliseconds: number }[];
    cues?: readonly { id: string; beforeText: string }[];
    timepoints?: readonly NarrationTimepoint[];
    /** A recording's word start times followed by its speech end, which keep subtitle progress on the spoken word. */
    wordBoundaries?: readonly number[];
    onFinished: () => void;
    onError: (message: string) => void;
    /** Return true only when visuals are ready and speech can cross the cue without interruption. */
    onMarker?: (index: number) => boolean | void;
    onSentence?: (index: number) => void;
};

export type NarrationTimelineControl = {
    cancel: () => void;
    pause: () => void;
    resume: () => void;
    setMuted: (muted: boolean) => void;
    /** The timeline position, when the control can report it between progress updates. */
    seconds?: () => number;
};

export type NarrationAudioRequest = {
    url: string;
    muted: boolean;
    timepoints?: readonly NarrationTimepoint[];
    onMarker: (index: number) => boolean | void;
    onProgress: (seconds: number, durationSeconds: number | undefined) => void;
    onEnd: () => void;
    onError: (message: string) => void;
};

type NarrationEnvironment = {
    schedule: (callback: () => void, milliseconds: number) => ReturnType<typeof setTimeout>;
    cancelTimer: (timer: ReturnType<typeof setTimeout>) => void;
    now: () => number;
    playAudio?: (request: NarrationAudioRequest) => NarrationTimelineControl;
    /** Mutes recorded narration while set, without pausing its timeline. */
    silence?: NarrationSilence;
};

/** A reading clock holds at unready markers, like a recording, until resumed. */
function startReadingClock(environment: NarrationEnvironment, plan: { durationMs: number; markersMs: readonly number[];
    sentenceStartsMs: readonly number[]; onMarker: (index: number) => boolean | void; onSentence: (index: number) => void; onEnd: () => void }): NarrationTimelineControl {
    let position = 0;
    let startedAt: number | undefined;
    let nextAt = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let marker = 0;
    let sentence = 1;
    let held = false;
    let paused = false;
    let ended = false;
    const fire = () => {
        if (marker < plan.markersMs.length && plan.markersMs[marker]! <= position) {
            held = plan.onMarker(marker++) !== true;
        }
        while (sentence < plan.sentenceStartsMs.length && plan.sentenceStartsMs[sentence]! <= position) plan.onSentence(sentence++);
        if (held) return;
        if (position >= plan.durationMs) {
            ended = true;
            plan.onEnd();
            return;
        }
        run();
    };
    const run = () => {
        if (paused || held || ended || timer !== undefined) return;
        const at = Math.min(plan.markersMs[marker] ?? Infinity, plan.sentenceStartsMs[sentence] ?? Infinity, plan.durationMs);
        startedAt = environment.now();
        nextAt = at;
        timer = environment.schedule(() => {
            timer = undefined;
            startedAt = undefined;
            position = Math.max(position, at);
            fire();
        }, Math.max(0, at - position));
    };
    const stop = () => {
        if (timer === undefined) return;
        environment.cancelTimer(timer);
        timer = undefined;
        position += environment.now() - (startedAt ?? environment.now());
        startedAt = undefined;
    };
    run();
    return {
        cancel: () => { ended = true; stop(); },
        pause: () => { paused = true; stop(); },
        resume: () => { paused = false; held = false; run(); },
        setMuted: () => {},
        seconds: () => (startedAt === undefined ? position
            : Math.min(nextAt, position + environment.now() - startedAt)) / 1000,
    };
}

export function createEpisodeNarrationOwner(environment: NarrationEnvironment) {
    let currentCue: EpisodeNarrationCue | undefined;
    let key: string | undefined;
    let control: NarrationTimelineControl | undefined;
    let paused = false;
    let generation = 0;
    let resumeSentence: (() => void) | undefined;
    let timeline: { sentences: readonly SubtitleSentence[]; anchors: readonly TimelineAnchor[]; seconds: number;
        cues: readonly NarrationTimepoint[] } | undefined;
    let unsubscribeSilence: (() => void) | undefined;
    const mutedFor = (cue: EpisodeNarrationCue) => !cue.sound || environment.silence?.isSilenced() === true;
    const dispose = () => {
        generation += 1;
        unsubscribeSilence?.();
        unsubscribeSilence = undefined;
        control?.cancel();
        control = undefined;
        key = undefined;
        paused = false;
        timeline = undefined;
        resumeSentence = undefined;
        currentCue = undefined;
    };
    const synchronize = (cue?: EpisodeNarrationCue) => {
        const recorded = Boolean(cue?.audioUrl && environment.playAudio);
        const nextKey = cue && `${cue.key}:${recorded ? cue.audioUrl : 'reading'}`;
        if (nextKey === key) {
            currentCue = cue;
            if (cue) control?.setMuted(mutedFor(cue));
            return;
        }
        dispose();
        if (!cue) return;
        currentCue = cue;
        if (cue.audioUrl && cue.sound && !recorded) {
            cue.onError('Recorded narration is unavailable in this browser. Turn sound off to continue.');
            return;
        }
        key = nextKey;
        const current = generation;
        const live = () => current === generation;
        let finished = false;
        const finish = () => {
            if (!live() || finished) return;
            finished = true;
            cue.onFinished();
        };
        if ((cue.displayText === undefined) !== (cue.narrationTextMap === undefined)) {
            throw new Error('Mapped narration requires both display text and a text mapping.');
        }
        const sentences = splitSubtitleSentences(cue.displayText ?? cue.text);
        const displayAnchors = (anchors: readonly TimelineAnchor[]) =>
            cue.narrationTextMap ? displayTimelineAnchors(anchors, cue.narrationTextMap) : anchors;
        let sentence: number | undefined;
        const showSentence = (index: number) => {
            if (!live() || index === sentence) return;
            if (paused) {
                resumeSentence = () => showSentence(index);
                return;
            }
            sentence = index;
            cue.onSentence?.(index);
        };
        const reachMarker = (index: number) => {
            if (!live() || paused) return false;
            const ready = currentCue?.onMarker?.(index) === true;
            if (!ready) paused = true;
            return ready;
        };
        const cues = cue.cues ?? [];
        showSentence(0);
        if (recorded) {
            const timepoints = cue.timepoints ?? [];
            const anchorsFor = (durationSeconds: number | undefined) =>
                displayAnchors(narrationTimelineAnchors(cue.text, cues, timepoints,
                    durationSeconds ?? cue.readingMs / 1000, cue.wordBoundaries));
            timeline = { sentences, anchors: anchorsFor(undefined), seconds: 0, cues: timepoints };
            control = environment.playAudio!({ url: cue.audioUrl!, muted: mutedFor(cue),
                ...(cue.onMarker && timepoints.length ? { timepoints } : {}),
                onMarker: reachMarker,
                onProgress: (seconds, durationSeconds) => {
                    if (!live()) return;
                    const anchors = anchorsFor(durationSeconds);
                    timeline = { sentences, anchors, seconds, cues: timepoints };
                    showSentence(subtitleSentenceAt(sentences, anchors, seconds));
                },
                onEnd: finish,
                onError: (message) => { if (live()) cue.onError(`Narration failed: ${message}`); } });
            unsubscribeSilence = environment.silence?.subscribe(() => {
                if (live() && currentCue) control?.setMuted(mutedFor(currentCue));
            });
            return;
        }
        const baseDuration = cue.readingMs / 1000;
        const durationSeconds = readingPositionSeconds(cue.text, cue.text.length, baseDuration, cue.narrationPauses);
        const timepoints = readingTimepoints(cue.text, cues, baseDuration, cue.narrationPauses);
        const anchors = displayAnchors(readingTimelineAnchors(cue.text, cues, baseDuration, cue.narrationPauses));
        timeline = { sentences, anchors, seconds: 0, cues: timepoints };
        control = startReadingClock(environment, { durationMs: durationSeconds * 1000,
            markersMs: cue.onMarker ? timepoints.map((point) => point.timeSeconds * 1000) : [],
            sentenceStartsMs: subtitleSentenceStarts(sentences, anchors).map((seconds) => seconds * 1000),
            onMarker: reachMarker, onSentence: showSentence, onEnd: finish });
    };
    const setPaused = (next: boolean) => {
        if (paused === next) return;
        paused = next;
        if (next) control?.pause();
        else {
            control?.resume();
            const show = resumeSentence;
            resumeSentence = undefined;
            show?.();
        }
    };
    /** Read on every animation frame by the subtitle band, so it never updates runtime state. */
    const spokenProgress = () => timeline && spokenSentenceProgress(timeline.sentences, timeline.anchors,
        control?.seconds?.() ?? timeline.seconds);
    const secondsSinceCue = (id: string) => {
        const point = timeline?.cues.find(point => point.markName === id);
        if (!point || !timeline) return undefined;
        const seconds = (control?.seconds?.() ?? timeline.seconds) - point.timeSeconds;
        return seconds < 0 ? undefined : seconds;
    };
    return { synchronize, dispose, setPaused, spokenProgress, secondsSinceCue };
}

export function createBrowserEpisodeNarrationOwner() {
    return createEpisodeNarrationOwner({
        schedule: (callback, milliseconds) => setTimeout(callback, milliseconds),
        cancelTimer: (timer) => clearTimeout(timer),
        now: () => performance.now(),
        silence: narrationSilence,
        ...(typeof Audio === 'undefined' ? {} : {
            playAudio: (request: NarrationAudioRequest): NarrationTimelineControl => {
                const { url, timepoints } = request;
                const audio = new Audio(url);
                audio.muted = request.muted;
                let active = true;
                let playGeneration = 0;
                let markerIndex = 0;
                let markerHeld = false;
                let explicitlyPaused = false;
                let endPending = false;
                let markerSeconds = 0;
                // Media seeks can round below the exact cue timestamp; never rewind across a reached cue.
                const seconds = () => Math.max(markerSeconds, audio.currentTime);
                const duration = () => Number.isFinite(audio.duration) && audio.duration > 0 ? audio.duration : undefined;
                const checkMarker = () => {
                    if (!active || markerHeld || explicitlyPaused) return;
                    let point = timepoints?.[markerIndex];
                    while (point && seconds() >= point.timeSeconds) {
                        const reachedPoint = point;
                        reportEpisodeDiagnostic('audio-marker', () => ({ url, markerIndex,
                            markName: reachedPoint.markName, expectedSeconds: reachedPoint.timeSeconds, actualSeconds: audio.currentTime }));
                        const ready = request.onMarker(markerIndex++);
                        if (ready === true) {
                            markerSeconds = point.timeSeconds;
                            point = timepoints?.[markerIndex];
                            continue;
                        }
                        markerHeld = true;
                        playGeneration++;
                        audio.pause();
                        audio.currentTime = point.timeSeconds;
                        markerSeconds = point.timeSeconds;
                        request.onProgress(point.timeSeconds, duration());
                        return;
                    }
                    request.onProgress(seconds(), duration());
                };
                const progressTimer = setInterval(checkMarker, 16);
                const release = () => {
                    active = false;
                    clearInterval(progressTimer);
                };
                audio.ontimeupdate = checkMarker;
                const finishAudio = () => {
                    if (explicitlyPaused) { endPending = true; return; }
                    endPending = false;
                    checkMarker();
                    if (active && !markerHeld) {
                        reportEpisodeDiagnostic('audio-ended', () => ({ url, audioSeconds: audio.currentTime }));
                        release();
                        request.onEnd();
                    }
                };
                audio.onended = finishAudio;
                audio.onerror = () => {
                    if (!active) return;
                    release();
                    request.onError(`Could not load recorded audio '${url}'.`);
                };
                const play = () => {
                    if (!active) return;
                    reportEpisodeDiagnostic('audio-play', () => ({ url, audioSeconds: audio.currentTime, markerIndex, muted: audio.muted }));
                    const attempt = ++playGeneration;
                    void audio.play().catch((error: unknown) => {
                        if (attempt !== playGeneration || !active) return;
                        release();
                        request.onError(error instanceof Error ? error.message : String(error));
                    });
                };
                play();
                return {
                    cancel: () => {
                        release();
                        audio.onended = null;
                        audio.onerror = null;
                        audio.ontimeupdate = null;
                        audio.pause();
                        audio.removeAttribute('src');
                        audio.load();
                    },
                    pause: () => { explicitlyPaused = true; playGeneration++; audio.pause(); },
                    resume: () => {
                        explicitlyPaused = false;
                        markerHeld = false;
                        if (endPending) finishAudio();
                        else play();
                    },
                    setMuted: (muted) => { audio.muted = muted; },
                    seconds,
                };
            },
        }),
    });
}
